'use client';

import { GlobalsCookieNotice } from '@/tina/__generated__/types';
import { sendGTMEvent } from '@next/third-parties/google';
import * as stylex from '@stylexjs/stylex';
import { Check } from 'lucide-react';
import Link from 'next/link';
import { useContext, useState } from 'react';
import { a11y } from '../../styles/a11y';
import { layout } from '../../styles/layout';
import '../../styles/tokens.stylex';
import { borderRadius, color, spacing } from '../../styles/tokens.stylex';
import { typography } from '../../styles/typography';
import { Button } from '../Buttons';
import { RichText } from '../RichText';
import { Switch } from '../Switch';
import { CookieNotificationContext } from './context';
import { SetCookieButton } from './notificationPopoverClientUi';
type CookieSettingProps = {
  data: GlobalsCookieNotice;
};
export function CookieSettings({
  data
}: CookieSettingProps) {
  const {
    updateConsent,
    closeSettings,
    permissionState,
    settingsRef
  } = useContext(CookieNotificationContext);
  const [activeConsentDescription, setActiveConsentDescription] = useState<string | null>(null);
  return !data ? null : <dialog ref={settingsRef} {...stylex.props(layout.contentCard, sx.dialog)}>
      <div className="x1o4oh2x x3ybb33 xrvj5dj x15g8yj6">
        <h1 {...stylex.props(typography.heading3)}>Cookie Settings</h1>
        <RichText components={{
        a: props => <Link {...stylex.props(typography.small)} href={props?.url ?? '#'}>
                {props?.children}
              </Link>,
        p: props => <p {...stylex.props(typography.small)} {...props} />
      }}>
          {data?.settings_message}
        </RichText>
      </div>
      <div className="x1g9dfjz xysyzu8 x78zum5 xdt5ytf x1qughib">
        <div className="x1ghz6dp x1717udv xng3xce xc342km xir93jf x1algulf x2p2wen xrvj5dj xs83m0k">
          {data.consent?.map(consent => {
          if (!consent?.id) return null;
          const isActive = activeConsentDescription === consent?.id;
          const toggle = () => {
            setActiveConsentDescription(isActive ? null : consent?.id);
            sendGTMEvent({
              event: isActive ? 'consent_description_close' : 'consent_description_open',
              consent: consent?.name
            });
          };
          return <div key={consent?.id} className="x16pmaob xcrclan xrvj5dj x1g3yg12 x5uqrmk">
                <Button variant="ghost" theme="neutral" iconOnly="chevron-down" iconOnlyStyles={[sx.icon, isActive && sx.faceUp]} onClick={toggle} styles={[sx.alignTop, sx.chevronButton]} id={consent?.id}>
                  Show Description
                </Button>
                <div className="x12qliqu">
                  <label htmlFor={consent?.id} {...stylex.props(typography.heading4)}>
                    {consent?.name}
                  </label>
                  <RichText components={{
                a: props => <Link {...stylex.props(typography.small)} href={props?.url ?? '#'}>
                          {props?.children}
                        </Link>,
                p: props => <p {...stylex.props(typography.small)} {...props} />
              }} wrapperStyles={[sx.consentDescription, !isActive && a11y.visuallyHidden]}>
                    {consent?.description}
                  </RichText>
                </div>
                {consent?.required ? <div className="x16rqkct x11x033 x1y5e3q9 x19i6xfm xuq06ny xrvj5dj x7v7x1q x10w6t97">
                    <Check />
                  </div> : <Switch styles={sx.alignTop} checked={permissionState[consent?.id] ?? false} onCheckedChange={checked => updateConsent(consent?.id, checked)} />}
              </div>;
        })}
        </div>
        <div className="x1o4oh2x x3ybb33 xrvj5dj xt2d6zu">
          <SetCookieButton decision="reject">Reject All</SetCookieButton>
          <Button onClick={closeSettings} variant="link" theme="neutral">
            Save Preferences
          </Button>
          <SetCookieButton decision="accept">Accept All</SetCookieButton>
        </div>
      </div>
    </dialog>;
}
const sx = {
  dialog: {
    kzIqYQ: "xd00j3c",
    kpwlN0: "xckjefj",
    kmVPX3: "x1717udv",
    kOIVth: "x1g9dfjz",
    kmkexE: "xoka680",
    kWkggS: "x1xlpcrp",
    k1xSpc: "xuw0eqa",
    k9llMU: "xogrr9c",
    kSiTet: "x4itv7f xg01cxk x19qbp35",
    k3aq6I: "xvg6mwh x1e03zdn xrni2di",
    kIyJzY: "x1d8287x",
    kY2c9j: "x1n327nk",
    kskxy: "xng76gn",
    ks0D6T: "x1319t99",
    k7Eaqz: "xaaagfd",
    $$css: true
  },
  consentDescription: {
    k1K539: "xat24cr",
    kLKAdn: "x10bx6wk",
    $$css: true
  },
  icon: {
    kmkexE: "x1ux5q4u",
    k3nNDw: "x1g0ag68",
    k6sLGO: "x1so62im",
    kZKoxP: "xam5rvr",
    k1K539: "xniua9b",
    kzqmXN: "xp8d6y2",
    $$css: true
  },
  faceUp: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  alignTop: {
    kSGwAc: "x1y8v6su",
    $$css: true
  },
  chevronButton: {
    kaIpWk: "xakuvmb",
    kAzted: "xjwf9q1",
    $$css: true
  }
};